import {noop} from '@benchling/js-utils';
import {useCallback, useEffect} from 'react';

import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import spreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {TableIndex} from 'notebook/tables/TableConstants';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';

/**
 * Implements the multi-window editing behavior of MultiSelectListUtil.
 *
 * This automatically adds click handlers to the window to handle multi-table editing.
 */
export default function useMultiWindowEditing({
  tableNamespace,
  fileId,
  domNode,
  tableName,
  multiStartIndex,
  multiEndIndex,
  hasFormulasDisabled,
  multiActive,
  multiEditing,
  multiCrossEditing,
  onEditFinalize,
  setState,
  canInsert,
  setMultiEditingForTable,
}: {
  tableNamespace: string;
  fileId: string;
  domNode: Element;
  tableName: string;
  multiStartIndex: TableIndex | null;
  multiEndIndex: TableIndex | null;
  hasFormulasDisabled: boolean;
  multiActive: string | boolean | null;
  multiEditing: boolean;
  multiCrossEditing: boolean;
  onEditFinalize: () => void;
  setState?: (state: Partial<MultiSelectListUtil.MultiSelectState<TableIndex>>) => void;
  // If canInsert/setMultiEditingForTable are not provided, they will automatically use the default
  // implementation from SpreadsheetEvalUtil.
  canInsert?: (e: {start: number; end: number; cid: string; shift: boolean}) => boolean;
  setMultiEditingForTable?: (
    tableName: string,
    e: {index: TableIndex | null; editing: boolean; text: string}
  ) => void;
}): void {
  const {editingTable} = useStoreSelector(spreadsheetStore, (store) => ({
    editingTable: store.editingTable,
  }));

  const multiCanInsert = useCallback(
    (e: {start: number; end: number; cid: string; shift: boolean}) => {
      if (canInsert) {
        return canInsert(e);
      } else {
        return SpreadsheetEvalUtil.canInsert({
          e,
          hasFormulasDisabled,
          tableNamespace,
          editingTable,
        });
      }
    },
    [tableNamespace, hasFormulasDisabled, editingTable, canInsert]
  );

  const setMultiEditing = useCallback(
    (args: {index: TableIndex | null; editing: boolean; text: string}) => {
      if (setMultiEditingForTable) {
        setMultiEditingForTable(tableName, args);
      } else {
        SpreadsheetEvalUtil.setMultiEditingForTable(tableName, tableNamespace, fileId, {
          index: args.index,
          editing: args.editing,
          text: args.text,
          typedValue: null,
          selection: null,
        });
      }
    },
    [tableNamespace, tableName, fileId, setMultiEditingForTable]
  );

  const _multiWindowMouseDownHandler = useCallback(
    (e: MouseEvent): void => {
      const options = {
        multiEditing,
        multiCanInsert,
        multiNamespace: tableNamespace,
        multiFinalizeEditHandler: onEditFinalize,
        domNode,
        multiStartIndex,
        multiEndIndex,
        setMultiEditing,
        setState: setState ?? noop,
        multiActive,
        multiCrossEditing,
      };
      MultiSelectListUtil.multiWindowMouseDownHandler(e, options);
    },
    [
      multiEditing,
      multiCanInsert,
      tableNamespace,
      onEditFinalize,
      domNode,
      multiStartIndex,
      multiEndIndex,
      setMultiEditing,
      setState,
      multiActive,
      multiCrossEditing,
    ]
  );

  const _multiWindowMouseUpHandler = useCallback(
    (e: MouseEvent): void => {
      const options = {
        multiEditing,
        multiCanInsert: canInsert,
        multiNamespace: tableNamespace,
        multiFinalizeEditHandler: onEditFinalize,
        domNode,
        multiStartIndex,
        multiEndIndex,
        setMultiEditing,
        setState: setState ?? noop,
        multiActive,
        multiCrossEditing,
      };
      MultiSelectListUtil.multiWindowMouseUpHandler(e, options);
    },
    [
      multiEditing,
      canInsert,
      tableNamespace,
      onEditFinalize,
      domNode,
      multiStartIndex,
      multiEndIndex,
      setMultiEditing,
      setState,
      multiActive,
      multiCrossEditing,
    ]
  );

  useEffect(() => {
    MultiSelectListUtil.multiSelectableDidMount(_multiWindowMouseDownHandler, _multiWindowMouseUpHandler);
    return () => {
      MultiSelectListUtil.multiSelectableWillUnmount(
        _multiWindowMouseDownHandler,
        _multiWindowMouseUpHandler
      );
    };
  }, [_multiWindowMouseDownHandler, _multiWindowMouseUpHandler]);
}
